import React from 'react';
import { useNavigate, useSearchParams } from '@remix-run/react';
import { useTrendingSearchResults } from '../hooks/use-trending-search-results';
import { useRecentlySearched } from '../hook/use-recently-searched';
import { generateURL } from '@modules-rocco/url/api/generate-url';
import { useSearchTracking } from '~/ui-rocco/hooks/use-search-tracking';

interface MobileSearchTrendingProps {
    /**
     * Context where the component is being used
     * - 'modal': Inside the mobile search modal (no padding)
     * - 'page': On the mobile search page (with padding)
     */
    context?: 'modal' | 'page';
}

/**
 * Renders the "Trending" list for mobile search modal.
 * Shows the top 5 trending search results with title and links.
 */
export const MobileSearchTrending: React.FC<MobileSearchTrendingProps> = ({
    context = 'page',
}) => {
    const navigate = useNavigate();
    const [, setSearchParams] = useSearchParams();
    const { trendingSearchResults } = useTrendingSearchResults();
    const { addRecentSearch } = useRecentlySearched();
    const trackingConfig = useSearchTracking({ isSearchPage: true });

    const handleSearchClick = (
        result: NonNullable<typeof trendingSearchResults>[number]
    ) => {
        // Add the trending item title to recently searched history
        addRecentSearch(result.title);

        // Map result type to search category
        const typeToCategory: Record<string, string> = {
            Blog: 'articles',
            Product: 'products',
            Professional: 'professionals',
            Project: 'projects',
        };

        const category = typeToCategory[result.type];
        const searchPath = category
            ? `/search/category_${category}`
            : '/search';

        // Perform a category-aware search
        setSearchParams({ search: result.title });
        navigate(
            generateURL(searchPath, {
                search: result.title,
            })
        );
        // Track the trending click
        trackingConfig.onSearchTrendingClick(result.title);
    };

    if (!trendingSearchResults?.length) {
        return null;
    }

    // Use padding only for search page context, not for modal
    const containerClassName = context === 'modal' ? '' : 'px-6 md:px-0';

    return (
        <div className={containerClassName}>
            <h3 className="text-base font-medium">Trending</h3>
            <ul className="my-4.5 space-y-2.5">
                {trendingSearchResults.map((result, idx) => (
                    <li key={idx} className="w-auto text-left">
                        <button
                            type="button"
                            className="block min-h-4.5 max-w-full text-left leading-[1.1] text-[#9b9b9b]"
                            onClick={() => {
                                handleSearchClick(result);
                            }}
                        >
                            {result.title}
                        </button>
                    </li>
                ))}
            </ul>
        </div>
    );
};
